import { createRouter, createWebHistory } from 'vue-router'
import { routerBase } from '../lib/runtime'
import { useAuthStore } from '../stores/auth'

const router = createRouter({
  history: createWebHistory(routerBase(true)),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('../layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('../views/DashboardView.vue'),
        },
        {
          path: 'bookings',
          name: 'bookings',
          component: () => import('../views/BookingsListView.vue'),
        },
        {
          path: 'bookings/new',
          name: 'booking-create',
          component: () => import('../views/CreateBookingView.vue'),
        },
        {
          path: 'bookings/:id',
          name: 'booking-detail',
          component: () => import('../views/BookingDetailView.vue'),
        },
        {
          path: 'floor-plan',
          name: 'floor-plan',
          component: () => import('../views/FloorPlanView.vue'),
        },
        {
          path: 'timeline',
          name: 'timeline',
          component: () => import('../views/TimelineView.vue'),
        },
        {
          path: 'floor-plan/edit',
          name: 'floor-plan-edit',
          component: () => import('../views/FloorPlanEditorView.vue'),
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('../views/CalendarView.vue'),
        },
        {
          path: 'sales-pricing',
          name: 'sales-pricing',
          component: () => import('../views/SalesPricingView.vue'),
          // Reads sales from the POS: the "connect your POS provider" banner shows here.
          meta: { needsPos: true },
        },
        // Old bookmarks. Square is connected in Hospora now, so there is no connect page (and no
        // OAuth landing at /square) any more: /integrations lands on the read-only sales view.
        {
          path: 'integrations',
          redirect: { name: 'sales-pricing' },
        },
        {
          path: 'tables',
          name: 'tables',
          component: () => import('../views/TableConfigView.vue'),
        },
        {
          path: 'open-hours',
          name: 'open-hours',
          component: () => import('../views/OpenHoursView.vue'),
        },
        {
          path: 'charge-rules',
          name: 'charge-rules',
          component: () => import('../views/ChargeRulesView.vue'),
          // Deposits and fees are taken through the POS's payments.
          meta: { needsPos: true },
        },
        {
          path: 'venues',
          name: 'venues',
          component: () => import('../views/VenueManagementView.vue'),
          meta: { adminOnly: true },
        },
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import('../views/InvoicesView.vue'),
          meta: { adminOnly: true },
        },
      ],
    },
  ],
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  // The shared entry point is /{slug}/admin. Keep a launch token in the
  // fragment until LoginView consumes it, even when replacing a local session.
  if (to.hash.includes('hospora_token=')) {
    if (to.name !== 'login') return { name: 'login', hash: to.hash }
    return
  }

  // If there is a token but it has expired since it was last checked, log out
  // so the user lands on the login page rather than seeing blank/broken data.
  if (auth.token && auth.isAuthenticated) {
    const payload = auth.token ? (() => {
      try {
        const parts = auth.token!.split('.')
        if (parts.length !== 3) return null
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        return JSON.parse(atob(base64)) as { exp?: number }
      } catch { return null }
    })() : null
    if (!payload?.exp || payload.exp < Math.floor(Date.now() / 1000)) {
      auth.logout()
      if (!to.meta.public) {
        return { name: 'login', query: { redirect: to.path } }
      }
    }
  }

  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.path } }
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
  if (to.meta.adminOnly && !auth.isAdmin) {
    return { name: 'dashboard' }
  }
})

export default router
